import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { EventModel } from '@app/events/shared/event.model';
import { provideFlatpickrDefaults } from 'angularx-flatpickr';
import { Download, Edit, FileText, LucideAngularModule, Paperclip, Trash, X } from 'lucide-angular';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { EventService } from '@app/events/shared/event.service';
import { AbstractEntityEditComponent } from '@app/shared/abstract/abstract-entity-edit.component';
import { ButtonComponent } from '@app/partials/form/button/button.component';
import { ModalComponent } from '@app/partials/modal/modal.component';
import { DatePipe } from '@angular/common';
import { Editor, NgxEditorModule, toDoc, toHTML, Toolbar } from 'ngx-editor';

@Component({
  selector: 'app-event-notes',
  templateUrl: './notes.component.html',
  styleUrls: [],
  providers: [provideFlatpickrDefaults()],
  imports: [ButtonComponent, ModalComponent, NgxEditorModule, LucideAngularModule, DatePipe, ReactiveFormsModule],
  standalone: true,
})
export class NotesComponent implements OnInit, OnDestroy {
  @Input() public event!: EventModel;
  @Output() toggleOpen = new EventEmitter<any>();

  protected readonly TrashIcon = Trash;
  protected readonly EditIcon = Edit;
  protected readonly PaperclipIcon = Paperclip;
  protected readonly XIcon = X;
  protected readonly DownloadIcon = Download;
  protected readonly FileTextIcon = FileText;

  protected editor?: Editor;
  protected toolbar: Toolbar = [
    ['bold', 'italic'],
    ['underline', 'strike'],
    ['ordered_list', 'bullet_list', 'link'],
    ['align_left', 'align_center', 'align_right', 'align_justify'],
    ['undo', 'redo'],
  ];

  protected form!: FormGroup<{
    note: FormControl<any>;
    attachment: FormControl<File | null>;
    remove_attachment: FormControl<boolean | null>;
  }>;
  protected validationErrors: string[] = [];

  protected processing = false;
  protected loading = false;
  protected loadError: string | null = null;

  protected isDeleteModalOpen = false;
  protected deleteNoteId: number | null = null;

  protected editNoteId: number | null = null;

  // Store URL for image preview
  protected previewUrl: string | null = null;

  constructor(private eventService: EventService) {}

  ngOnInit(): void {
    this.editor = new Editor({
      history: true,
      keyboardShortcuts: true,
    });

    this.form = new FormGroup({
      note: new FormControl<any>('', {
        validators: [Validators.required],
      }),
      attachment: new FormControl<File | null>(null),
      remove_attachment: new FormControl<boolean | null>(null),
    });

    this.form.valueChanges.subscribe((value) => {
      if (this.form.dirty && !this.form.valid) {
        this.validationErrors = this.getFormValidationErrors(this.form);
      }
    });

    this.loadNotes();
  }

  ngOnDestroy(): void {
    this.editor?.destroy();
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
    }
  }

  private loadNotes(): void {
    if (!this.event?.id) {
      return;
    }

    this.loading = true;
    this.loadError = null;
    this.event.notes = this.event.notes ?? [];

    this.eventService.getEventNotes(this.event.id).subscribe({
      next: (response) => {
        this.event.notes = response.data ?? [];
        this.sortNotes();
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        this.loadError = 'Failed to load notes.';
        console.error('Error loading notes:', error);
      },
    });
  }

  save() {
    if (!this.form) {
      return;
    }
    if (this.form.invalid) {
      return;
    }

    const data = this.form.value as any;

    const noteValue = this.form.get('note')?.value;
    if (noteValue && typeof noteValue === 'object') {
      data.note = toHTML(noteValue);
    } else {
      data.note = noteValue || '';
    }

    if (data.attachment === null) {
      delete data.attachment;
    }
    if (data.remove_attachment === null) {
      delete data.remove_attachment;
    }

    data.type = 'internal';
    data.noteable_type = 'event';
    data.noteable_id = this.event.id;

    this.processing = true;

    if (this.editNoteId) {
      data.id = this.editNoteId;
      this.eventService.updateEventNote(data, this.editNoteId).subscribe({
        next: (response) => {
          const index = this.event.notes.findIndex((note) => note.id === this.editNoteId);
          if (index !== -1) {
            this.event.notes[index] = response.data;
          }
          this.sortNotes();
          this.form.reset();
          this.editNoteId = null;
          this.processing = false;
        },
        error: (res) => {
          if (res.status === 422) {
            AbstractEntityEditComponent.bindErrorsToForm(res.error, this.form);
          }
          this.processing = false;
        },
      });
    } else {
      this.eventService.createEventNote(data).subscribe({
        next: (response) => {
          this.event.notes.unshift(response.data);
          this.sortNotes();
          this.form.reset();
          this.processing = false;
        },
        error: (res) => {
          if (res.status === 422) {
            AbstractEntityEditComponent.bindErrorsToForm(res.error, this.form);
          }
          this.processing = false;
        },
      });
    }
  }

  getFormValidationErrors(form: FormGroup): string[] {
    const errors: string[] = [];
    Object.keys(form.controls).forEach((key) => {
      const controlErrors = form.get(key)?.errors;
      if (controlErrors) {
        Object.keys(controlErrors).forEach((errorKey) => {
          errors.push(`${key} has error: ${errorKey}`);
        });
      }
    });
    return errors;
  }

  deleteNote(id: any) {
    this.isDeleteModalOpen = true;
    this.deleteNoteId = id;
  }

  confirmDelete() {
    if (this.deleteNoteId) {
      this.eventService.deleteEventNote(this.deleteNoteId).subscribe({
        next: () => {
          this.event.notes = this.event.notes.filter((note) => note.id !== this.deleteNoteId);
          this.isDeleteModalOpen = false;
          this.deleteNoteId = null;
        },
        error: (res) => {
          console.error('Error deleting note:', res);
        },
      });
    }
  }

  cancel() {
    this.form?.reset();
    this.toggleOpen.emit();
  }

  cancelEdit() {
    // Clean up preview URL if exists
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = null;
    }

    this.editNoteId = null;
    this.form.reset();
  }

  editNote(id: number) {
    this.editNoteId = id;
    const note = this.event.notes.find((note) => note.id === id);
    if (note) {
      this.form.patchValue({
        note: note.note ? toDoc(note.note) : '',
        remove_attachment: false,
      });

      if (note.has_attachment && note.attachment) {
        this.form.controls.attachment.setValue({
          name: note.attachment.original_name,
        } as any);
      }
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      const file = input.files[0];
      this.form.patchValue({
        attachment: file,
        remove_attachment: false,
      });
      this.form.markAsDirty();

      // Create preview URL if it's an image file
      if (this.isImageFile(file)) {
        this.previewUrl = URL.createObjectURL(file);
      } else {
        this.previewUrl = null;
      }
    }
  }

  removeAttachment(): void {
    // Revoke object URL if one exists to prevent memory leaks
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = null;
    }

    this.form.patchValue({
      attachment: null,
      remove_attachment: true,
    });
    this.form.markAsDirty();
  }

  cancelAttachmentRemoval(): void {
    this.form.patchValue({
      remove_attachment: false,
    });
  }

  downloadAttachment(noteId: number): void {
    const note = this.event.notes.find((n) => n.id === noteId);
    if (!note || !note.has_attachment) return;

    this.eventService.downloadNoteAttachment(noteId).subscribe({
      next: (response) => {
        const filename =
          this.extractFilenameFromContentDisposition(response.headers.get('Content-Disposition')) ||
          note.attachment?.original_name ||
          'attachment';

        const blob = response.body as Blob;
        if (!blob || blob.size === 0) {
          console.error('No file content received');
          return;
        }

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      },
      error: (error) => {
        console.error('Error downloading attachment:', error);
      }
    });
  }

  private extractFilenameFromContentDisposition(contentDisposition: string | null): string | null {
    if (!contentDisposition) return null;

    // RFC 6266 supports both filename and filename* (for encoded values)
    const filenameStarMatch = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(contentDisposition);
    if (filenameStarMatch?.[1]) {
      try {
        return decodeURIComponent(filenameStarMatch[1]);
      } catch {
        return filenameStarMatch[1];
      }
    }

    const filenameMatch = /filename\s*=\s*\"?([^\\";]+)\"?/i.exec(contentDisposition);
    return filenameMatch?.[1] ? filenameMatch[1] : null;
  }

  getFileIcon(note: any) {
    if (!note.has_attachment || !note.attachment) return this.FileTextIcon;

    const fileType = note.attachment.file_type;
    return this.FileTextIcon; // Default icon, could expand with different icons based on file type
  }

  getFileName(file: any): string {
    return file?.name || '';
  }

  /**
   * Checks if the given file is an image based on its MIME type
   */
  isImageFile(file: File | null): boolean {
    if (!file) return false;
    return file.type.startsWith('image/');
  }

  viewFullImage(imageUrl: string): void {
    if (imageUrl) {
      window.open(imageUrl, '_blank');
    }
  }

  onImageError(event: any, note: any): void {
    console.warn('Failed to load image thumbnail, falling back to original URL', note.attachment);
    if (note.attachment?.url && event.target.src !== note.attachment.url) {
      event.target.src = note.attachment.url;
    } else {
      event.target.style.display = 'none';
    }
  }

  private sortNotes(): void {
    if (!this.event?.notes?.length) return;

    this.event.notes = [...this.event.notes].sort((a: any, b: any) => {
      const createdCompare = String(b?.created_at ?? '').localeCompare(String(a?.created_at ?? ''));
      if (createdCompare !== 0) return createdCompare;

      return Number(b?.id ?? 0) - Number(a?.id ?? 0);
    });
  }
}
